import { faCheck, faChevronRight, faGlobe } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useMemo, useRef, useState } from "react";
import { useDismissOnOutside, useEscapeToClose } from "../../Hook/dismiss";
import { FormattedMessage } from "react-intl";
import useLocale from "../../Hook/locale";
import usePlatform from "../../Hook/platform";
import Sheet from "../Sheet";

/**
 * The locale selector (Shared chrome board, turn 4b): a globe and the language
 * written in its own language — no flags, because a locale is a language and
 * not a country, and no image from an external CDN.
 *
 * One component, two mounts:
 * - header  a pill in the desktop header, opening a popover at the ThemePicker
 *           layer, anchored to its right edge.
 * - row     a line in the You hub, opening a bottom sheet on the sheet layer.
 *
 * The list comes from the platform payload in payload order, both mounts, and
 * picking applies immediately — the current locale wears the accent check.
 */

/** The language named in itself — Français for fr whatever the app speaks.
    Cached (BC6): Intl.DisplayNames is one of the pricier constructors and the
    header calls this on every render. */
const NATIVE_NAMES = new Map<string, string>();
function nativeName(iso: string): string {
  const cached = NATIVE_NAMES.get(iso);
  if (cached) return cached;
  let name = iso.toUpperCase();
  try {
    const resolved = new Intl.DisplayNames([iso], { type: "language" }).of(iso);
    if (resolved && resolved !== iso) name = resolved.charAt(0).toUpperCase() + resolved.slice(1);
  } catch {
    /* unknown tag — the code will have to do */
  }
  NATIVE_NAMES.set(iso, name);
  return name;
}

function useLocaleList() {
  const platform = usePlatform();
  const locale = useLocale();

  const entries = useMemo(
    () =>
      (platform?.platform?.languages ?? [])
        .filter((language) => language.type === "front")
        .map((language) => ({ iso: language.iso, name: nativeName(language.iso) })),
    [platform?.platform?.languages],
  );

  return { entries, current: locale.locale, pick: locale.changeLang };
}

function LocaleOptionList({
  list,
  onPicked,
  rowClass,
}: {
  /** From the mount's own useLocaleList — one subscription per surface (BC6). */
  list: ReturnType<typeof useLocaleList>;
  onPicked?: () => void;
  rowClass?: string;
}) {
  const { entries, current, pick } = list;
  return (
    <>
      {entries.map((entry) => {
        const active = entry.iso === current;
        return (
          <button
            key={entry.iso}
            type="button"
            data-testid={`locale-option-${entry.iso}`}
            onClick={() => {
              pick(entry.iso);
              onPicked?.();
            }}
            className={
              "w-full flex items-center gap-3 text-left rounded-md hover:bg-tint-hover " +
              (rowClass ?? "px-3 py-2.5") +
              (active ? " text-accent font-medium" : " text-text")
            }
          >
            <span className="flex-1 min-w-0 truncate text-row">{entry.name}</span>
            {active && <FontAwesomeIcon className="text-[12px]" icon={faCheck} />}
          </button>
        );
      })}
    </>
  );
}

/** The desktop header mount: pill trigger, right-anchored popover. */
function HeaderLocale() {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const list = useLocaleList();
  const { entries, current } = list;

  useDismissOnOutside(open, root, () => setOpen(false));

  if (!entries.length) return null;
  const currentName = nativeName(current);

  return (
    <div className="relative" ref={root}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        aria-haspopup="listbox"
        data-testid="locale-trigger"
        className={
          "flex items-center gap-2 h-9 px-3 rounded-pill text-row " +
          (open ? "bg-tint-pill text-accent" : "bg-tint-neutral text-text hover:bg-tint-hover")
        }
      >
        <FontAwesomeIcon className="text-[13px]" icon={faGlobe} />
        <span className="max-w-[92px] truncate">{currentName}</span>
      </button>

      {open && (
        <div
          className="absolute right-0 top-11 z-popover-panel w-[208px] p-1.5 rounded-panel bg-card shadow-sheet flex flex-col"
          role="listbox"
          data-testid="locale-panel"
        >
          <LocaleOptionList list={list} onPicked={() => setOpen(false)} />
        </div>
      )}
    </div>
  );
}

/** The You-hub mount: a row stating the current locale, opening a bottom sheet. */
function RowLocale() {
  const [open, setOpen] = useState(false);
  const list = useLocaleList();
  const { entries, current } = list;

  if (!entries.length) return null;

  return (
    <>
      <button
        type="button"
        data-testid="locale-row"
        onClick={() => setOpen(true)}
        className="w-full flex items-center gap-3 px-4 py-3 min-h-tap-min text-left hover:bg-tint-hover"
      >
        <FontAwesomeIcon className="w-[22px] text-muted" icon={faGlobe} />
        <div className="flex-1 min-w-0 text-row">
          <FormattedMessage id="locale__label" />
        </div>
        <div className="text-meta text-muted">{nativeName(current)}</div>
        <FontAwesomeIcon className="text-muted text-[13px]" icon={faChevronRight} />
      </button>

      {open && (
        <Sheet onClose={() => setOpen(false)}>
          <div className="text-sheet-title mb-2 flex-none">
            <FormattedMessage id="locale__label" />
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto flex flex-col" data-testid="locale-sheet">
            <LocaleOptionList list={list} onPicked={() => setOpen(false)} rowClass="px-2 py-3" />
          </div>
        </Sheet>
      )}
    </>
  );
}

function LocaleSelector({
  mount = "header",
  headerPosition,
}: {
  mount?: "header" | "row";
  /** Legacy prop from the old dropdown — means the header mount. */
  headerPosition?: boolean;
}) {
  if (mount === "row" && !headerPosition) return <RowLocale />;
  return <HeaderLocale />;
}

export default LocaleSelector;
